import FunnelsAPI from 'dashboard/api/funnels';
import FunnelItemsAPI from 'dashboard/api/funnelItems';

const state = {
  records: [],
  currentFunnel: null,
  stages: [],
  items: {},
  stageStats: {},
  perStageLimit: null,
  automations: [],
  pulsingItemIds: [],
  uiFlags: {
    isFetching: false,
    isFetchingStages: false,
    isFetchingItems: false,
    isFetchingAutomations: false,
    isSavingAutomation: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

const getters = {
  getFunnels: _state => _state.records,
  getCurrentFunnel: _state => _state.currentFunnel,
  getStages: _state => _state.stages,
  getItemsByStage: _state => stageId => _state.items[stageId] || [],
  getAllItems: _state => Object.values(_state.items).flat(),
  getStageStats: _state => _state.stageStats,
  getPerStageLimit: _state => _state.perStageLimit,
  getAutomations: _state => _state.automations,
  getUIFlags: _state => _state.uiFlags,
  getFunnelById: _state => id => _state.records.find(f => f.id === Number(id)),
  isItemPulsing: _state => itemId => _state.pulsingItemIds.includes(itemId),
};

const mutations = {
  SET_FUNNELS(_state, funnels) {
    _state.records = funnels;
  },
  SET_CURRENT_FUNNEL(_state, funnel) {
    _state.currentFunnel = funnel;
  },
  ADD_FUNNEL(_state, funnel) {
    _state.records.push(funnel);
  },
  UPDATE_FUNNEL(_state, funnel) {
    const index = _state.records.findIndex(f => f.id === funnel.id);
    if (index !== -1) {
      _state.records.splice(index, 1, funnel);
    }
    if (_state.currentFunnel?.id === funnel.id) {
      _state.currentFunnel = funnel;
    }
  },
  DELETE_FUNNEL(_state, id) {
    _state.records = _state.records.filter(f => f.id !== id);
    if (_state.currentFunnel?.id === id) {
      _state.currentFunnel = null;
    }
  },
  SET_STAGES(_state, stages) {
    _state.stages = stages;
  },
  ADD_STAGE(_state, stage) {
    _state.stages.push(stage);
    _state.items[stage.id] = [];
  },
  UPDATE_STAGE(_state, stage) {
    const index = _state.stages.findIndex(s => s.id === stage.id);
    if (index !== -1) {
      _state.stages.splice(index, 1, stage);
    }
  },
  DELETE_STAGE(_state, stageId) {
    _state.stages = _state.stages.filter(s => s.id !== stageId);
    delete _state.items[stageId];
  },
  SET_ITEMS(_state, { stageId, items }) {
    _state.items = { ..._state.items, [stageId]: items };
  },
  SET_ALL_ITEMS(_state, itemsByStage) {
    _state.items = itemsByStage;
  },
  SET_STAGE_STATS(_state, stats) {
    _state.stageStats = stats;
  },
  SET_PER_STAGE_LIMIT(_state, limit) {
    _state.perStageLimit = limit;
  },
  ADD_ITEM(_state, item) {
    const stageId = item.funnel_stage_id;
    if (!_state.items[stageId]) {
      _state.items[stageId] = [];
    }
    _state.items[stageId].push(item);
  },
  UPDATE_ITEM(_state, item) {
    const stageId = item.funnel_stage_id;
    if (_state.items[stageId]) {
      const index = _state.items[stageId].findIndex(i => i.id === item.id);
      if (index !== -1) {
        _state.items[stageId].splice(index, 1, item);
      }
    }
  },
  DELETE_ITEM(_state, { stageId, itemId }) {
    if (_state.items[stageId]) {
      _state.items[stageId] = _state.items[stageId].filter(
        i => i.id !== itemId
      );
    }
  },
  UPDATE_ITEMS_BY_CONVERSATION(_state, { conversationId, updates }) {
    // Find item once and stop (avoids scanning all stages)
    const stageIds = Object.keys(_state.items);
    for (let i = 0; i < stageIds.length; i += 1) {
      const stageItems = _state.items[stageIds[i]];
      const idx = stageItems.findIndex(
        item => item.conversation_id === conversationId
      );
      if (idx !== -1) {
        stageItems.splice(idx, 1, { ...stageItems[idx], ...updates });
        return;
      }
    }
  },
  MOVE_ITEM(_state, { item, fromStageId, toStageId }) {
    // Remove from old stage
    if (_state.items[fromStageId]) {
      _state.items[fromStageId] = _state.items[fromStageId].filter(
        i => i.id !== item.id
      );
    }
    // Add to new stage
    if (!_state.items[toStageId]) {
      _state.items[toStageId] = [];
    }
    _state.items[toStageId].push({ ...item, funnel_stage_id: toStageId });
    // Sort by position
    _state.items[toStageId].sort((a, b) => a.position - b.position);
  },
  REORDER_ITEM(_state, { stageId, itemId, newPosition }) {
    if (!_state.items[stageId]) return;

    const items = [..._state.items[stageId]];
    const itemIndex = items.findIndex(i => i.id === itemId);
    if (itemIndex === -1) return;

    // Remove item from current position
    const [item] = items.splice(itemIndex, 1);

    // Insert at new position
    items.splice(newPosition, 0, item);

    // Update positions for all items
    items.forEach((i, idx) => {
      i.position = idx;
    });

    _state.items[stageId] = items;
  },
  ADD_PULSING_ITEM(_state, itemId) {
    if (!_state.pulsingItemIds.includes(itemId)) {
      _state.pulsingItemIds.push(itemId);
    }
  },
  REMOVE_PULSING_ITEM(_state, itemId) {
    _state.pulsingItemIds = _state.pulsingItemIds.filter(id => id !== itemId);
  },
  CLEAR_PULSING_ITEMS(_state) {
    _state.pulsingItemIds = [];
  },
  SET_AUTOMATIONS(_state, automations) {
    _state.automations = automations;
  },
  ADD_AUTOMATION(_state, automation) {
    _state.automations.push(automation);
  },
  UPDATE_AUTOMATION(_state, automation) {
    const index = _state.automations.findIndex(a => a.id === automation.id);
    if (index !== -1) {
      _state.automations.splice(index, 1, automation);
    }
  },
  DELETE_AUTOMATION(_state, id) {
    _state.automations = _state.automations.filter(a => a.id !== id);
  },
  SET_UI_FLAG(_state, { key, value }) {
    _state.uiFlags[key] = value;
  },
};

const actions = {
  async fetchFunnels({ commit }) {
    commit('SET_UI_FLAG', { key: 'isFetching', value: true });
    try {
      const { data } = await FunnelsAPI.get();
      commit('SET_FUNNELS', data);
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isFetching', value: false });
    }
  },

  async fetchFunnel({ commit }, id) {
    commit('SET_UI_FLAG', { key: 'isFetching', value: true });
    try {
      const { data } = await FunnelsAPI.show(id);
      commit('SET_CURRENT_FUNNEL', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isFetching', value: false });
    }
  },

  async createFunnel({ commit }, funnelData) {
    commit('SET_UI_FLAG', { key: 'isCreating', value: true });
    try {
      const { data } = await FunnelsAPI.create(funnelData);
      commit('ADD_FUNNEL', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isCreating', value: false });
    }
  },

  async updateFunnel({ commit }, { id, ...funnelData }) {
    commit('SET_UI_FLAG', { key: 'isUpdating', value: true });
    try {
      const { data } = await FunnelsAPI.update(id, funnelData);
      commit('UPDATE_FUNNEL', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isUpdating', value: false });
    }
  },

  async deleteFunnel({ commit }, id) {
    commit('SET_UI_FLAG', { key: 'isDeleting', value: true });
    try {
      await FunnelsAPI.delete(id);
      commit('DELETE_FUNNEL', id);
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isDeleting', value: false });
    }
  },

  // Stages
  async fetchStages({ commit }, funnelId) {
    commit('SET_UI_FLAG', { key: 'isFetchingStages', value: true });
    try {
      const { data } = await FunnelsAPI.getStages(funnelId);
      commit('SET_STAGES', data);
      // Initialize items for each stage
      const itemsByStage = {};
      data.forEach(stage => {
        itemsByStage[stage.id] = [];
      });
      commit('SET_ALL_ITEMS', itemsByStage);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isFetchingStages', value: false });
    }
  },

  async createStage({ commit }, { funnelId, ...stageData }) {
    try {
      const { data } = await FunnelsAPI.createStage(funnelId, stageData);
      commit('ADD_STAGE', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateStage({ commit }, { funnelId, stageId, ...stageData }) {
    try {
      const { data } = await FunnelsAPI.updateStage(
        funnelId,
        stageId,
        stageData
      );
      commit('UPDATE_STAGE', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async deleteStage({ commit }, { funnelId, stageId }) {
    try {
      await FunnelsAPI.deleteStage(funnelId, stageId);
      commit('DELETE_STAGE', stageId);
    } catch (error) {
      throw error;
    }
  },

  async reorderStages({ commit }, { funnelId, stageIds }) {
    try {
      const { data } = await FunnelsAPI.reorderStages(funnelId, stageIds);
      commit('SET_STAGES', data);
    } catch (error) {
      throw error;
    }
  },

  // Items
  async fetchItems(
    { commit, state: moduleState },
    { funnelId, stageId, dateFrom, dateTo, perStage }
  ) {
    commit('SET_UI_FLAG', { key: 'isFetchingItems', value: true });
    try {
      const params = { stage_id: stageId };

      // Pass date filters to backend when explicitly set
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (perStage) params.per_stage = perStage;

      const { data } = await FunnelsAPI.getItems(funnelId, params);

      // Support new format { items: [...], meta: {...} } and legacy array format
      const items = data.items || data;
      const meta = data.meta || {};

      if (meta.stage_stats) {
        commit('SET_STAGE_STATS', meta.stage_stats);
      }
      if (meta.per_stage_limit) {
        commit('SET_PER_STAGE_LIMIT', meta.per_stage_limit);
      }

      if (stageId) {
        commit('SET_ITEMS', { stageId, items });
      } else {
        // Group items by stage, preserving existing empty stages
        const itemsByStage = {};
        Object.keys(moduleState.items).forEach(sid => {
          itemsByStage[sid] = [];
        });
        items.forEach(item => {
          if (!itemsByStage[item.funnel_stage_id]) {
            itemsByStage[item.funnel_stage_id] = [];
          }
          itemsByStage[item.funnel_stage_id].push(item);
        });
        commit('SET_ALL_ITEMS', itemsByStage);
      }
      return items;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isFetchingItems', value: false });
    }
  },

  async createItem({ commit }, { funnelId, ...itemData }) {
    try {
      const { data } = await FunnelsAPI.createItem(funnelId, itemData);
      commit('ADD_ITEM', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateItem({ commit }, { funnelId, itemId, ...itemData }) {
    try {
      const { data } = await FunnelsAPI.updateItem(funnelId, itemId, itemData);
      commit('UPDATE_ITEM', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async deleteItem({ commit }, { funnelId, stageId, itemId }) {
    try {
      await FunnelsAPI.deleteItem(funnelId, itemId);
      commit('DELETE_ITEM', { stageId, itemId });
    } catch (error) {
      throw error;
    }
  },

  async moveItem(
    { commit, state: _state },
    { funnelId, itemId, fromStageId, toStageId, position }
  ) {
    const item = _state.items[fromStageId]?.find(i => i.id === itemId);
    if (!item) return;

    // Optimistic update
    commit('MOVE_ITEM', { item, fromStageId, toStageId });

    try {
      const { data } = await FunnelsAPI.moveItem(
        funnelId,
        itemId,
        toStageId,
        position
      );
      commit('UPDATE_ITEM', data);
      return data;
    } catch (error) {
      // Rollback on error
      commit('MOVE_ITEM', {
        item: { ...item, funnel_stage_id: fromStageId },
        fromStageId: toStageId,
        toStageId: fromStageId,
      });
      throw error;
    }
  },

  async reorderItem(
    { commit, state: _state },
    { funnelId, itemId, stageId, position }
  ) {
    const items = _state.items[stageId] || [];
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const oldPosition = item.position;

    // Optimistic update
    commit('REORDER_ITEM', { stageId, itemId, newPosition: position });

    try {
      const { data } = await FunnelsAPI.moveItem(
        funnelId,
        itemId,
        stageId,
        position
      );
      commit('UPDATE_ITEM', data);
      return data;
    } catch (error) {
      // Rollback on error
      commit('REORDER_ITEM', { stageId, itemId, newPosition: oldPosition });
      throw error;
    }
  },

  // Real-time updates from ActionCable (funnel item events)
  handleRealtimeItemCreated({ commit, state: _state }, data) {
    const funnelId = data.funnel_id;
    if (_state.currentFunnel?.id !== funnelId) return;

    const stageId = data.funnel_stage_id;
    if (!_state.items[stageId]) return;

    // Avoid duplicates
    const exists = _state.items[stageId].some(i => i.id === data.id);
    if (exists) return;

    commit('ADD_ITEM', data);
    commit('ADD_PULSING_ITEM', data.id);
  },

  handleRealtimeItemMoved({ commit, state: _state }, data) {
    const funnelId = data.funnel_id;
    if (_state.currentFunnel?.id !== funnelId) return;

    const fromStageId = data.from_stage_id;
    const toStageId = data.to_stage_id;

    // Find existing item across all stages
    let existingItem = null;
    const stageIds = Object.keys(_state.items);
    for (let i = 0; i < stageIds.length; i += 1) {
      existingItem = _state.items[stageIds[i]].find(item => item.id === data.id);
      if (existingItem) break;
    }

    if (existingItem) {
      commit('MOVE_ITEM', { item: data, fromStageId, toStageId });
    } else if (_state.items[toStageId]) {
      commit('ADD_ITEM', { ...data, funnel_stage_id: toStageId });
    }
    commit('ADD_PULSING_ITEM', data.id);
  },

  stopItemPulsing({ commit }, itemId) {
    commit('REMOVE_PULSING_ITEM', itemId);
  },

  clearAllPulsing({ commit }) {
    commit('CLEAR_PULSING_ITEMS');
  },

  // Real-time updates from ActionCable (conversation events)
  updateFunnelItemFromMessage({ commit, getters: _getters }, { conversationId, message, lastActivityAt }) {
    const allItems = _getters.getAllItems;
    const hasItem = allItems.some(item => item.conversation_id === conversationId);
    if (!hasItem) return;

    const updates = {
      conversation_last_activity_at: lastActivityAt,
    };
    if (message) {
      updates.last_message = {
        content: message.content?.substring(0, 100),
        message_type: message.message_type,
        created_at: message.created_at,
      };
    }
    commit('UPDATE_ITEMS_BY_CONVERSATION', { conversationId, updates });
  },

  updateFunnelItemFromConversation({ commit, getters: _getters }, conversation) {
    // conversation.id from push_event_data is already the display_id
    const conversationId = conversation.id;
    const allItems = _getters.getAllItems;
    const hasItem = allItems.some(
      item => item.conversation_id === conversationId
    );
    if (!hasItem) return;

    const updates = {};
    if (conversation.status) updates.conversation_status = conversation.status;
    if (conversation.last_activity_at) {
      updates.conversation_last_activity_at = conversation.last_activity_at;
    }
    if (conversation.meta?.assignee) {
      updates.conversation_assignee_id = conversation.meta.assignee.id;
      updates.conversation_assignee = {
        id: conversation.meta.assignee.id,
        name: conversation.meta.assignee.name,
        avatar_url: conversation.meta.assignee.avatar_url,
      };
    }
    commit('UPDATE_ITEMS_BY_CONVERSATION', { conversationId, updates });
  },

  // Link funnel items by phone number when a new conversation arrives
  linkFunnelItemByPhone({ commit, getters: _getters }, conversation) {
    const senderPhone = conversation.meta?.sender?.phone_number;
    if (!senderPhone) return;

    const cleanPhone = senderPhone.replace(/[^\d+]/g, '');
    if (!cleanPhone) return;

    const allItems = _getters.getAllItems;
    const matchedItem = allItems.find(item => {
      if (item.contact_id || item.conversation_id) return false;
      const rawPhone = item.custom_attributes?.phone_number || '';
      const itemPhone = rawPhone.replace(/[^\d+]/g, '');
      if (!itemPhone) return false;
      return (
        itemPhone === cleanPhone ||
        cleanPhone.endsWith(itemPhone) ||
        itemPhone.endsWith(cleanPhone)
      );
    });

    if (!matchedItem) return;

    const conversationId = conversation.id;
    const contact = {
      id: conversation.meta?.sender?.id,
      name: conversation.meta?.sender?.name,
      phone_number: senderPhone,
      avatar_url: conversation.meta?.sender?.avatar_url,
    };

    const updates = {
      contact_id: contact.id,
      contact,
      conversation_id: conversationId,
      conversation_status: conversation.status,
      conversation_last_activity_at: conversation.last_activity_at,
    };

    if (conversation.meta?.assignee) {
      updates.conversation_assignee_id = conversation.meta.assignee.id;
      updates.conversation_assignee = {
        id: conversation.meta.assignee.id,
        name: conversation.meta.assignee.name,
        avatar_url: conversation.meta.assignee.avatar_url,
      };
    }

    // Update item in-place via UPDATE_ITEM mutation
    commit('UPDATE_ITEM', { ...matchedItem, ...updates });
  },

  // Nested resources (using FunnelItemsAPI)
  async fetchItemChecklists(_, { accountId, itemId }) {
    const { data } = await FunnelItemsAPI.getChecklists(accountId, itemId);
    return data;
  },

  async createItemChecklist(_, { accountId, itemId, ...checklistData }) {
    const { data } = await FunnelItemsAPI.createChecklist(
      accountId,
      itemId,
      checklistData
    );
    return data;
  },

  async toggleItemChecklist(_, { accountId, itemId, checklistId }) {
    const { data } = await FunnelItemsAPI.toggleChecklist(
      accountId,
      itemId,
      checklistId
    );
    return data;
  },

  async deleteItemChecklist(_, { accountId, itemId, checklistId }) {
    await FunnelItemsAPI.deleteChecklist(accountId, itemId, checklistId);
  },

  async fetchItemNotes(_, { accountId, itemId }) {
    const { data } = await FunnelItemsAPI.getNotes(accountId, itemId);
    return data;
  },

  async createItemNote(_, { accountId, itemId, content }) {
    const { data } = await FunnelItemsAPI.createNote(
      accountId,
      itemId,
      content
    );
    return data;
  },

  async deleteItemNote(_, { accountId, itemId, noteId }) {
    await FunnelItemsAPI.deleteNote(accountId, itemId, noteId);
  },

  async fetchItemParticipants(_, { accountId, itemId }) {
    const { data } = await FunnelItemsAPI.getParticipants(accountId, itemId);
    return data;
  },

  async addItemParticipant(_, { accountId, itemId, userId }) {
    const { data } = await FunnelItemsAPI.addParticipant(
      accountId,
      itemId,
      userId
    );
    return data;
  },

  async removeItemParticipant(_, { accountId, itemId, participantId }) {
    await FunnelItemsAPI.removeParticipant(accountId, itemId, participantId);
  },

  async fetchItemLabels(_, { accountId, itemId }) {
    const { data } = await FunnelItemsAPI.getLabels(accountId, itemId);
    return data;
  },

  async addItemLabel(_, { accountId, itemId, labelId }) {
    const { data } = await FunnelItemsAPI.addLabel(accountId, itemId, labelId);
    return data;
  },

  async removeItemLabel(_, { accountId, itemId, labelId }) {
    await FunnelItemsAPI.removeLabel(accountId, itemId, labelId);
  },

  async fetchItemActivities(_, { accountId, itemId }) {
    const { data } = await FunnelItemsAPI.getActivities(accountId, itemId);
    return data;
  },

  // Automations
  async fetchAutomations({ commit }, funnelId) {
    commit('SET_UI_FLAG', { key: 'isFetchingAutomations', value: true });
    try {
      const { data } = await FunnelsAPI.getAutomations(funnelId);
      commit('SET_AUTOMATIONS', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isFetchingAutomations', value: false });
    }
  },

  async createAutomation({ commit }, { funnelId, ...automationData }) {
    commit('SET_UI_FLAG', { key: 'isSavingAutomation', value: true });
    try {
      const { data } = await FunnelsAPI.createAutomation(
        funnelId,
        automationData
      );
      commit('ADD_AUTOMATION', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isSavingAutomation', value: false });
    }
  },

  async updateAutomation(
    { commit },
    { funnelId, automationId, ...automationData }
  ) {
    commit('SET_UI_FLAG', { key: 'isSavingAutomation', value: true });
    try {
      const { data } = await FunnelsAPI.updateAutomation(
        funnelId,
        automationId,
        automationData
      );
      commit('UPDATE_AUTOMATION', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { key: 'isSavingAutomation', value: false });
    }
  },

  async deleteAutomation({ commit }, { funnelId, automationId }) {
    try {
      await FunnelsAPI.deleteAutomation(funnelId, automationId);
      commit('DELETE_AUTOMATION', automationId);
    } catch (error) {
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
